import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, filter, firstValueFrom, map, Observable, of, race, Subscription, switchMap, timer } from 'rxjs';
import { AuthService, REAUTH_REQUIRED_ERROR_CODES } from '../core/firebase/auth.service';
import { ProfileStateDispatcher } from '../core/state/profile/profile.dispatcher';
import { SopoLoadingService } from '../shared/services/sopo-loading/sopo-loading.service';
import { Auth, getAuth, multiFactor, onIdTokenChanged, PhoneMultiFactorInfo, updateEmail, user, User } from '@angular/fire/auth';
import { doc, docData, DocumentReference, Firestore, getDoc } from '@angular/fire/firestore';
import { GeolocationService } from '../shared/services/geolocation/geolocation.service';
import { DEFAULT_DISMISS_AFTER_TIME, SopoPopupService } from '../sopo-popup/service/sopo-popup.service';
import { SopoAlertSettings } from '../sopo-popup/interfaces/alert';
import { SopoConfirmSettings } from '../sopo-popup/interfaces/confirm';
import { FeatureService } from '../core/firebase/feature.service';
import { LocationAlertComponent } from '../shared/components/location-alert/location-alert.component';
import { isElectron } from '../shared/utils/platform.utils';
import { NavController, ToastController } from '@ionic/angular';
import { ApiService } from '../core/backend/api.service';
import { environment } from 'src/environments/environment';
import { SopoVerifyDataSettings } from '../sopo-popup/components/sopo-verify-info/sopo-verify-info.component';
import { IEditVerifiedUserDataPayload, IKycData, IVerified1ClickPayload, IVerifiedUserData, PersonaVerificationStatus, VerifiedIncStatus, VerifiedDataStatus } from '@soposopo/sopo-models';
import Persona from 'persona';
import { ZendeskService } from '../shared/services/zendesk/zendesk.service';
import { TwitterPixelService } from '../shared/services/twitter-pixel/twitter-pixel.service';
import { SopoDialogRef, SopoDialogSettings } from '../sopo-popup/interfaces/dialog';

const DOC_WAITING_TIME = 5000;
declare const zE: any;
export const LOCATION_DISMISS_TIME = 7000;

export const DEFAULT_KYC_REQUIRED_DIALOG_SETTINGS: SopoDialogSettings = {
    backdropDismiss: false,
    closeButton: false,
    sCss: ['sopo-popup-kyc-required-container'],
    zIndex: 10001,
}

@Injectable({
    providedIn: 'root'
})
export class VerificationService {
    private emailVerified = new BehaviorSubject(null);
    private twoFactorAuthentication = new BehaviorSubject(null);
    private verifiedIncVerified = new BehaviorSubject(null);
    private personaVerified = new BehaviorSubject(null);
    private verifiedDataStatus: VerifiedDataStatus;
    private personaStatus: PersonaVerificationStatus;
    private verifiedIncStatus: VerifiedIncStatus;

    emailVerificationSubscription;
    twoFactorSubscription: Subscription;

    constructor(
        public route: ActivatedRoute,
        private sopoPopupService: SopoPopupService,
        private locationService: GeolocationService,
        private authService: AuthService,
        private profileStore: ProfileStateDispatcher,
        private sopoLoadingService: SopoLoadingService,
        private firestore: Firestore,
        private profileDispatcher: ProfileStateDispatcher,
        private auth: Auth,
        private featureService: FeatureService,
        private toastCtrl: ToastController,
        private nav: NavController,
        private apiService: ApiService,
        private zendeskService: ZendeskService,
        private twitterPixelService: TwitterPixelService,
    ) {
        this.initEmailSubscription();
        this.initObservables();
    }

    private async initEmailSubscription() {
        this.emailVerificationSubscription = onIdTokenChanged(this.auth, async (user) => {
            const profileState = await firstValueFrom(this.profileDispatcher.getProfileState());
            const profileData = profileState.profileData;

            if (user && user.email && user.emailVerified) {
                if (profileData && profileData.email && profileData.emailVerified) {
                    this.emailVerified.next(true);
                } else if (profileData && profileData.email && !profileData.emailVerified) {
                    try {
                        await this.apiService.setUserEmailVerified(user.uid);
                        this.emailVerified.next(true);
                    } catch(error) {
                        console.error('Could not set email verification in profile data!', error);
                    }
                } else {
                    this.emailVerified.next(false);
                }
            }
            else {
                this.emailVerified.next(false);
            }
        });
    }

    private async initObservables() {
        const currentUser$ = user(this.auth);
        const mappedProfileState$ = this.profileStore.getProfileState().pipe(
            map(profileState => ({test2faEnabled: profileState.profileData.test2faEnabled})),
            distinctUntilChanged((prev, curr) => prev.test2faEnabled === curr.test2faEnabled)
        );

        this.twoFactorSubscription = currentUser$.pipe(
            map(user => {
                return user?.uid
            }),
            distinctUntilChanged(),
            switchMap(userId => {
                if (!userId) return of([null, null, undefined]);
                const docRef = doc(this.firestore, `kyc/${userId}`);
                const kycDoc$ = docData(docRef).pipe(
                    distinctUntilChanged(),
                    catchError(error => {
                        console.error('Error fetching kyc document:', error);
                        return of(null);
                    })
                );
                return combineLatest([currentUser$, mappedProfileState$, kycDoc$])
            })).subscribe(([user, profileData, kycDoc]) => {
                if (!user) {
                    this.twoFactorAuthentication.next(false);
                    this.verifiedIncVerified.next(false);
                    this.personaVerified.next(false);
                    return;
                }

                if (profileData?.test2faEnabled) {
                    this.twoFactorAuthentication.next(true);
                    this.verifiedIncVerified.next(true);
                    this.personaVerified.next(true);
                    return;
                }

                const hasMultifactor = multiFactor(user).enrolledFactors.length !== 0;
                this.twoFactorAuthentication.next(hasMultifactor);

                if (!hasMultifactor) {
                    this.twoFactorAuthentication.next(false);
                    this.verifiedIncVerified.next(false);
                    this.personaVerified.next(false);
                    return;
                }

                if (!kycDoc || !kycDoc.phoneNumber) {
                    this.verifiedIncVerified.next(false);
                    this.personaVerified.next(false);
                    return;
                }

                this.verifiedIncStatus = kycDoc?.verifiedIncStatus;
                this.verifiedDataStatus = kycDoc?.verifiedDataStatus;
                this.personaStatus = kycDoc?.personaStatus;

                const isVerifiedIncVerified = this.verifiedDataStatus === VerifiedDataStatus.ACCEPTED;
                this.verifiedIncVerified.next(isVerifiedIncVerified);

                const isPersonaVerified = this.personaStatus === PersonaVerificationStatus.SUCCESS;
                this.personaVerified.next(isPersonaVerified);
            }
        );
    }

    isPhoneVerified(): Observable<boolean> {
        return this.twoFactorAuthentication.asObservable().pipe(
            filter(isVerified => isVerified !== null),
            distinctUntilChanged()
        );
    }

    isEmailVerified(): Observable<boolean> {
        return this.emailVerified.asObservable().pipe(
            filter(isVerified => isVerified !== null),
            distinctUntilChanged()
        );
    }

    isVerifiedIncVerified(): Observable<boolean> {
        return this.verifiedIncVerified.asObservable().pipe(
            filter(isVerified => isVerified !== null),
            distinctUntilChanged()
        );
    }

    isPersonaVerified(): Observable<boolean> {
        return this.personaVerified.asObservable().pipe(
            filter(isPersonaVerified => isPersonaVerified !== null),
            distinctUntilChanged()
        );
    }

    isFullyVerified(): boolean {
        return this.emailVerified.value === true &&
            this.twoFactorAuthentication.value === true &&
            this.verifiedIncVerified.value === true &&
            this.personaVerified.value === true;
    }
    
    async verifyEmail() {
        if (this.emailVerified.value) return;

        const user = getAuth().currentUser;
        const profileState = await firstValueFrom(this.profileDispatcher.getProfileState());

        if (!user || !profileState.profileData) {
            this.handleEmailError(new Error('Could not fetch profile data.'));
            return;
        }

        const userEmail = user.email;
        const docEmail = profileState.profileData.email;

        try {
            if (userEmail && docEmail) {
                await this.handleBothEmailsExist();
            } else if (userEmail && !docEmail) {
                await this.handleEmailInAuthOnly(user);
            } else if (!userEmail && docEmail) {
                await this.handleEmailInDocOnly(user.uid, docEmail);
            } else {
                await this.handleNoEmailAnywhere(user.uid);
            }
        } catch (error) {
            this.sopoLoadingService.dismiss('auth');
            this.handleEmailError(error);
        }
    }

    private async handleBothEmailsExist() {
        const resend = await this.sopoPopupService.promptForConfirmation('app', {
            data: {
                header: 'Email Verification Required',
                body: 'A verification email has been sent to your address. If you have not received it, you may request a new one.',
                cancelText: 'Dismiss',
                okText: 'Resend Email'
            }
        });
        if (resend) {
            await this.authService.sendEmailVerification();
        }
    }

    private async handleEmailInAuthOnly(user: User) {
        const email = await this.promptUserForEmail(user.email);
        if (!email) return;

        if (user.email === email) {
            try {
                this.sopoLoadingService.present('auth');
                await this.apiService.setUserEmail(user.uid);
            } catch (error) {
                this.handleEmailError(new Error('Could not save email, please try again later.'));
                return;
            }
            this.sopoLoadingService.dismiss('auth');
            await this.authService.sendEmailVerification();
        } else {
            await this.saveEmailInAuthAndDoc(user.uid, email);
        }
    }

    private async handleEmailInDocOnly(userId: string, docEmail: string) {
        const email = await this.promptUserForEmail(docEmail);
        if (!email) return;

        if (docEmail === email) {
            this.sopoLoadingService.present('auth');
            try {
                await updateEmail(getAuth().currentUser, email);
            } catch (error) {
                if (error.code && REAUTH_REQUIRED_ERROR_CODES.includes(error.code)) {
                    this.sopoLoadingService.dismiss('auth');
                    await this.authService.verifyProvider();
                    try {
                        await updateEmail(getAuth().currentUser, email);
                    } catch (error2) {
                        if (error2.code) this.handleEmailError(error2);
                        else this.handleEmailError(new Error('Could not save email, please try again later.'));
                        return;
                    }
                } else {
                    if (error.code) this.handleEmailError(error);
                    else this.handleEmailError(new Error('Could not save email, please try again later.'));
                    return;
                }
            }
            this.sopoLoadingService.dismiss('auth');
            await this.authService.sendEmailVerification();
        } else {
            await this.saveEmailInAuthAndDoc(userId, email);
        }
    }

    private async handleNoEmailAnywhere(userId: string) {
        const email = await this.promptUserForEmail();

        if (!email) return;

        await this.saveEmailInAuthAndDoc(userId, email);
    }

    private async promptUserForEmail(prefilled?: string): Promise<string | null> {
        return this.sopoPopupService.promptForString('app', {
            closeButton: true,
            data: {
                header: 'Verify Your Email Address',
                body: 'Enter your email address below to receive a verification link.',
                placeholder: 'email',
                okText: 'Send',
                email: true,
                prefilledString: prefilled,
            }
        });
    }

    async saveEmailInAuthAndDoc(userId: string, emailAddress: string) {
        this.sopoLoadingService.present('auth');
        try {
            await updateEmail(getAuth().currentUser, emailAddress);
        } catch (error) {
            if (error.code && REAUTH_REQUIRED_ERROR_CODES.includes(error.code)) {
                this.sopoLoadingService.dismiss('auth');
                await this.authService.verifyProvider();
                try {
                    await updateEmail(getAuth().currentUser, emailAddress);
                } catch(error2) {
                    if (error2.code) this.handleEmailError(error2);
                    else this.handleEmailError(new Error('Could not save email, please try again later.'));
                    return;
                }
            } else {
                if (error.code) this.handleEmailError(error);
                else this.handleEmailError(new Error('Could not save email, please try again later.'));
                return;
            }
        }

        try {
            await this.apiService.setUserEmail(userId);
        } catch (error) {
            if (error.code) this.handleEmailError(error);
            else this.handleEmailError(new Error('Could not save email, please try again later.'));
            return;
        }
        this.sopoLoadingService.dismiss('auth');
        await this.authService.sendEmailVerification();
    }

    async enrollTwoFactor() {
        if (this.twoFactorAuthentication.value) {
            return;
        }

        if (!this.emailVerified.value) {
            this.showAlert({header: 'Email Verification Needed', message: 'You need to verify your email before enrolling in Two Factor Authentication.'});
            return;
        }

        const profileState = await firstValueFrom(this.profileDispatcher.getProfileState());

        if (!profileState?.profileData) {
            this.handleEmailError(new Error('Could not fetch profile data.'));
            return;
        }

        await this.authService.enrollTwoFactorAuthentication(true);
        this.sopoLoadingService.dismiss('auth');
    }

    async verifiedIncVerification() {
        if (this.verifiedIncVerified.value) {
            return;
        }
        if (!this.twoFactorAuthentication.value) {
            this.showAlert({header: 'Two Factor Authentication Needed', message: 'You need to enable two factor authentication before verifying phone number.'});
            return;
        }

        const profileState = await firstValueFrom(this.profileDispatcher.getProfileState());
        let phoneNumber = await this.getPhoneNumber(profileState.profileData.uid);

        if (!phoneNumber) {
            const factor = multiFactor(getAuth().currentUser).enrolledFactors[0] as PhoneMultiFactorInfo;
            const enrolledPhoneNumber = factor.phoneNumber;
            try {
                this.sopoLoadingService.present('api');
                await this.apiService.savePhoneNumber(profileState.profileData.uid, {phoneNumber: enrolledPhoneNumber});
                phoneNumber = enrolledPhoneNumber;
                this.sopoLoadingService.dismiss('api');
            } catch (error) {
                this.sopoLoadingService.dismiss('api');
                console.error('[VerificationService] - Could not save phone number!');
                this.showAlert({header: 'Verification error', message: 'Could not save phone number, please try again later.'});
                return;
            }
        }

        let email = await this.getEmailAddress(profileState.profileData.uid);

        if (!email) {
            try {
                this.sopoLoadingService.present('api');
                await this.apiService.setUserEmailVerified(profileState.profileData.uid);
                this.sopoLoadingService.dismiss('api');
            } catch (error) {
                this.sopoLoadingService.dismiss('api');
                console.error('[VerificationService] - Could not save email address!');
                this.showAlert({header: 'Verification error', message: 'Could not save email address, please try again later.'});
                return;
            }
        }

        if (!this.verifiedIncStatus || !this.verifiedDataStatus) {
            const options: SopoConfirmSettings = {
                data: {
                    header: `Phone Number Verification`,
                    okText: `Verify`,
                    verifiedLanguage: true,
                }
            };
            const promptResponse = await this.sopoPopupService.promptForConfirmation('app', options);

            if (promptResponse) {
                const verifiedResult = await this.verifyPhoneWithVerified(phoneNumber);
                if (verifiedResult) {
                    this.sopoLoadingService.dismiss('auth');
                    await this.confirmInformation(profileState.profileData.uid)
                }
            }
        }
        //VerifiedIncStatus SUCCESS and verifiedDataStatus: PENDING
        else if (this.verifiedIncStatus === VerifiedIncStatus.SUCCESS && (this.verifiedDataStatus === VerifiedDataStatus.PENDING || this.verifiedDataStatus === VerifiedDataStatus.CANCELED)){
            await this.confirmInformation(profileState.profileData.uid);
        }
        //VerifiedIncStatus FAILED and verifiedDataStatus: PENDING
        else if (this.verifiedIncStatus === VerifiedIncStatus.FAILED && this.verifiedDataStatus === VerifiedDataStatus.PENDING){
            await this.manualSignup(profileState.profileData.uid, phoneNumber);
        }
    }

    async personaVerification() {
        if (this.personaVerified.value) {
            return;
        }
        if (!this.twoFactorAuthentication.value || !this.verifiedIncVerified.value) {
            this.showAlert({header: 'Additional Steps Required', message: 'Please complete all the required steps above before starting your Persona verification.'});
            return;
        }

        if (!this.personaStatus || this.personaStatus === PersonaVerificationStatus.PENDING) {
            const returnHome = await this.sopoPopupService.promptForConfirmation('app', { data: {
                header: 'Verification In Progress',
                body: 'Your Persona verification is currently pending. Please check back later to see if it has been approved.',
                okText: 'Return Home'
            }});
            if (returnHome) {
                this.nav.navigateRoot('/lobby/home');
            }
            return;
        }

        if (this.personaStatus === PersonaVerificationStatus.ON_REVIEW) {
            const returnHome = await this.sopoPopupService.promptForConfirmation('app', { data: {
                header: 'Under Manual Review',
                body: 'Your verification is being reviewed manually. We’ll notify you once a decision has been made. Please check back later.',
                okText: 'Return Home'
            }});
            if (returnHome) {
                this.nav.navigateRoot('/lobby/home');
            }
            return;
        }

        if (this.personaStatus === PersonaVerificationStatus.DECLINED) {
            const openSupport = await this.sopoPopupService.promptForConfirmation('app', { data: {
                header: 'Verification Unsuccessful',
                body: 'Unfortunately, your verification was declined. Please reach out to support for assistance.',
                okText: 'Contact Support'
            }});
            if (openSupport) {
                if (this.zendeskService.enableZendesk) {
                    zE('messenger', 'open');
                }
                else {
                    this.showAlert({header: 'Contact Support Unsuccessful', message: 'Zendesk is not available.'});
                }
            }
            return;
        }

        if (this.personaStatus === PersonaVerificationStatus.FAILED) {
            const openSupport = await this.sopoPopupService.promptForConfirmation('app', { data: {
                header: 'Verification Unsuccessful',
                body: 'There has been an error in your verification process. Please reach out to support for assistance.',
                okText: 'Contact Support'
            }});
            if (openSupport) {
                if (this.zendeskService.enableZendesk) {
                    this.zendeskService.openSupportChat();
                }
                else {
                    this.showAlert({header: 'Contact Support Unsuccessful', message: 'Zendesk is not available.'});
                }
            }
            return;
        }

        //INQUIRY REQUIRED
        const inquiryRequired = await this.sopoPopupService.promptForConfirmation('app', { data: {
            header: 'KYC Inquiry Required',
            body: 'To continue, you will need to complete a KYC verification.',
            okText: 'Start'
        }});

        const profileState = await firstValueFrom(this.profileDispatcher.getProfileState());
        const userId = profileState.profileData.uid;

        if (inquiryRequired) {
            await this.verifyPersona(userId);
        }
    }

    async confirmInformation(userId: string) {
        const verifiedData = await this.getVerifiedUserData(userId, true);
        const phoneNumber = await this.getPhoneNumber(userId);

        if (!verifiedData) {
            this.showAlert({header: 'Data Confirmation Error', message: 'Cannot fetch verified data, please try again later.'});
            console.error('[VerificationService] - No verified data found.');
            return;
        }

        if (!phoneNumber) {
            this.showAlert({header: 'Data Confirmation Error', message: 'Cannot fetch phone number, please try again later.'});
            console.error('[VerificationService] - Could not load phone number!');
            return;
        }

        if (this.verifiedDataStatus === VerifiedDataStatus.ACCEPTED) {
            return;
        }

        const birthDateISO = verifiedData?.birthDate ? verifiedData.birthDate.toDate().toISOString() : null;
        const dialogOptions: SopoVerifyDataSettings = {
            data: {
                okText: 'Confirm',
                cancelText: 'Cancel',
                city: verifiedData.address.city ? verifiedData.address.city : null,
                country: verifiedData.address.country ? verifiedData.address.country : null,
                firstName: verifiedData.firstName ? verifiedData.firstName : null,
                middleName: verifiedData.middleName ? verifiedData.middleName : null,
                lastName: verifiedData.lastName ? verifiedData.lastName : null,
                birthDate: verifiedData.birthDate ? birthDateISO : null,
                addressLine1: verifiedData.address.line1 ? verifiedData.address.line1 : null,
                state: verifiedData.address.state ? verifiedData.address.state : null,
                zip: verifiedData.address.zipCode ? verifiedData.address.zipCode : null,
                phone: phoneNumber,
                ssn: verifiedData.ssn ? verifiedData.ssn : null,
            },
        }

        const response = await this.sopoPopupService.promptForVerifyDataInput('app', dialogOptions);

        //CANCELED
        if (!response) {
            this.showAlert({header: 'Data Confirmation Error', message: 'Verification is canceled, please try again.'});

            try {
                await this.apiService.setUserVerifiedDataStatusCanceled(userId);
            } catch(error) {
                this.showAlert({header: 'Data Confirmation Error', message: 'Something went wrong, please try again later. If it’s been longer than 24 hours please create a support ticket.'});
                console.error('[ApiService] - set user verified status CANCELED failed!', error);
            }
            return;
        }

        if (response.isEdited) {
            const verifiedResponse = response.verifiedData;

            const verifiedDataResponse: IEditVerifiedUserDataPayload = {
                firstName: verifiedResponse.firstName ? verifiedResponse.firstName : '',
                middleName: verifiedResponse.middleName ? verifiedResponse.middleName : '',
                lastName: verifiedResponse.lastName ? verifiedResponse.lastName : '',
                birthDate: verifiedResponse.birthDate ? (new Date(verifiedResponse.birthDate)).getTime() : null,
                address: {
                    city: verifiedResponse.city ?? '',
                    country: verifiedResponse.country ?? '',
                    line1: `${verifiedResponse.addressLine1 ?? ''} ${verifiedResponse.addressLine2 ?? ''}`.trim(),
                    state: verifiedResponse.state ?? '',
                    zipCode: verifiedResponse.zip ?? '',
                },
                ssn: verifiedResponse.ssn ?? '',
            };

            try {
                await this.apiService.editVerifiedUserData(verifiedDataResponse, userId);
            } catch(error) {
                console.error('[ApiService] - edit verified data failed!', error);
                this.showAlert({header: 'Data Confirmation Error', message: 'Something went wrong, please try again later. If it’s been longer than 24 hours please create a support ticket.'});
                return;
            }
        }

        //ACCEPTED
        try {
            await this.apiService.setUserVerifiedDataStatusAccepted(userId);
        } catch (error) {
            if (error.error.message && error.error.message === 'An error occured while saving your data, please try again!') {
                this.showAlert({header: 'Data Confirmation Error', message: 'An error occured while saving your data, please try again.'});
            } else {
                console.error('[ApiService] - set user verified status ACCEPTED failed!', error);
                this.showAlert({header: 'Data Confirmation Error', message: 'Something went wrong, please try again later. If it’s been longer than 24 hours please create a support ticket.'});
            }
        }
    }

    async getVerifiedUserData(userId: string, shouldWait = false): Promise<IVerifiedUserData | null> {
        this.sopoLoadingService.present('firestore');
        try {
            const docRef = doc(this.firestore, `kyc/${userId}`) as DocumentReference<IKycData>;

            if (shouldWait) {
                const kycDoc$ = docData(docRef).pipe(filter(data => data.verifiedData !== undefined));
                const timer$: Observable<null> = timer(DOC_WAITING_TIME).pipe(map(() => null));
                const res = await firstValueFrom(race(kycDoc$, timer$));
                this.sopoLoadingService.dismiss('firestore');
                return res.verifiedData ? res.verifiedData : null;
            }

            const data = (await getDoc(docRef)).data();
            this.sopoLoadingService.dismiss('firestore');
            return data ? data.verifiedData : null;
        } catch (error) {
            this.sopoLoadingService.dismiss('firestore');
            console.error('Error fetching verified user data:', error);
            return null;
        }
    }

    private async getPhoneNumber(userId: string): Promise<string | null> {
        try {
            const docRef = doc(this.firestore, `kyc/${userId}`) as DocumentReference<IKycData>;
            const docSnap = await getDoc(docRef);
            const data = docSnap.data();

            const phoneNumber = data?.phoneNumber ?? null;
            return phoneNumber;
        } catch (error) {
            console.error('Error fetching verified phone number:', error);
            return null;
        }
    }

    private async getEmailAddress(userId: string): Promise<string | null> {
        try {
            const docRef = doc(this.firestore, `kyc/${userId}`) as DocumentReference<IKycData>;
            const docSnap = await getDoc(docRef);
            const data = docSnap.data();

            const email = data?.email ?? null;
            return email;
        } catch (error) {
            console.error('Error fetching email address:', error);
            return null;
        }
    }

    handleEmailError(error) {
        this.sopoPopupService.clearViewContainer('app');
        this.sopoLoadingService.dismiss('auth');

        const header = 'Email Verification Error'

        if (!error.code) {
            if (error.message === 'Could not fetch profile data.') {
                this.showAlert({header: header, message: error.message});
            }
            else if (error.message === 'Could not save email, please try again later.') {
                this.showAlert({header: header, message: error.message});
            }
            else {
                console.error('[VerificationService] - Error code is undefined!', error);
                const message = environment.staging ? 'Please try again.' + '<br/>' + `(${error.code})` : 'Please try again.';
                this.showAlert({header: header, message: message})
            }
        }
        else {
            switch (error.code) {
                case 'auth/email-already-in-use':
                    this.showAlert({header: header, message: 'This email address is already in use. Please use a different one.'});
                    break;
                case 'auth/requires-recent-login':
                    this.showAlert({header: header, message: 'Please log in again to update your email.'});
                    break;
                case 'auth/too-many-requests':
                    this.showAlert({header: header, message: 'Too many attempts, please try again later.'});
                    break;
                default:
                    console.error('[VerificationService] - ', error);
                    const message = environment.staging ? 'Please try again.' + '<br/>' + `(${error.code})` : 'Please try again.';
                    this.showAlert({
                        header: 'An error occured',
                        message: message
                    });
                    break;
                }
        }
    }

    async tryToPassSweepstakesGuard(): Promise<boolean> {
        let isAllowedLocation: boolean;

        try {
            isAllowedLocation = await this.locationService.isAllowedLocation();
            if (!isAllowedLocation) {
                const dialogRef = this.sopoPopupService.openDialog('app', LocationAlertComponent, {
                    closeButton: true,
                    zIndex: 2001,
                });
                return false;
            }
        } catch (error) {
            const message = isElectron()
                ? (error?.message || 'We couldn\'t verify your location. Please scan the QR code from the mobile app to refresh your location.')
                : 'We couldn\'t detect your location. Please allow location access in your browser settings.';
            this.sopoPopupService.showAlert('app', {
                header: isElectron() ? 'Location Verification Needed' : 'Location Access Needed',
                message,
                dismissAfter: LOCATION_DISMISS_TIME,
            });
            return false;
        }

        if (this.isFullyVerified()) {
            return true;
        }

        const dialogRef: SopoDialogRef | null = this.sopoPopupService.openKYCRequiredDialog('app', DEFAULT_KYC_REQUIRED_DIALOG_SETTINGS);
        if (dialogRef) await firstValueFrom(dialogRef.afterClosed);
        return false;
    }

    async verifyPhoneWithVerified(phoneNumber: string): Promise<boolean> {
        try {
            this.sopoPopupService.clearViewContainer('app');
            const profileState = await firstValueFrom(this.profileStore.getProfileState());

            if (!profileState || !profileState.profileData || !profileState.profileData.email || !profileState.profileData.uid) {
                return false;
            }

            //FOR TESTING!!!
            //phoneNumber = '+1222555000';

            const payload: IVerified1ClickPayload = {
                userId: profileState.profileData.uid,
                phone: phoneNumber,
                email: profileState.profileData.email
            }

            this.sopoLoadingService.present('api');
            try {
                const response = await this.apiService.verified1Click(payload);
                this.sopoLoadingService.dismiss('api');
                return true;
            } catch (error) {
                const errorData = error?.error?.payload?.data;
                const errorCode = errorData?.errorCode;
                this.sopoLoadingService.dismiss('api');

                if (errorData && errorCode) {
                    switch (errorCode) {
                        case 'OCE002': {
                            //Unsupported phone number
                            console.debug('Unsupported phone number, starting manual flow!');
                            const manualSignupSuccess = await this.manualSignup(profileState.profileData.uid, phoneNumber);
                            return false;
                        }
                        case 'OCE011': {
                            //Phone and Additional Info Required
                            console.debug('Verified - Additional info needed ', errorCode);
                            const additionalInfoSuccess = await this.promptForInformation(payload, errorData);
                            return additionalInfoSuccess;
                        }
                        case 'OCE013': {
                            //No Credentials Found
                            console.debug('No credentials found, starting manual flow!');
                            const manualSignupSuccess = await this.manualSignup(profileState.profileData.uid, phoneNumber);
                            return false;
                        }
                        case 'OCE017': {
                            //Blocked, risk too high
                            console.debug('Blocked, risk too high, starting manual flow!');
                            const manualSignupSuccess = await this.manualSignup(profileState.profileData.uid, phoneNumber);
                            return false;
                        }
                        case 'OCE019': {
                            //Maximum attempts exceeded
                            console.debug('Maximum attempts exceeded, starting manual flow!');
                            const manualSignupSuccess = await this.manualSignup(profileState.profileData.uid, phoneNumber);
                            return false;
                        }
                        default: {
                            this.showAlert({header: 'Verified call failed', message:  'Please try again later.'});
                            console.error(error);
                        }
                    }
                }
                else {
                    if (error.error.message && error.error.message === 'Email or phone number is already in use!') {
                        this.showAlert({header: 'Verified call failed', message: 'Email or phone number is already in use.'});
                    }
                    else this.showAlert({header: 'Verified call failed', message: 'Please try again later.'});
                    console.error(error);
                }
                return false;
            }
        } catch (error) {
            console.error(error);
            this.sopoLoadingService.dismiss('api');
            return false;
        }
    }

    async promptForInformation(verifiedPayload: IVerified1ClickPayload, errorData: any, alreadyTried = false, showManualSignup = false): Promise<boolean> {
        console.debug('Error additional inputs:', errorData.additionalInputs);
        const additionalInputs = errorData.additionalInputs as string[];

        let birthDateNeeded = false;
        let ssn4Needed = false;
        let firstNameNeeded = false;

        if (additionalInputs) {
            birthDateNeeded = additionalInputs.includes('birthDate');
            ssn4Needed = additionalInputs.includes('ssn4');
            firstNameNeeded = additionalInputs.includes('fullName.firstName');

            const additionalInfo = await this.sopoPopupService.promptForVerifyAdditionalDataInput('app', {
                data: {
                    birthDateNeeded: birthDateNeeded,
                    ssn4Needed: ssn4Needed,
                    firstNameNeeded: firstNameNeeded,
                    alreadyTried: alreadyTried,
                    showManualSignup,
                }
            });

            let requestData: any;

            if (additionalInfo) {
                if (additionalInfo.manualSignup) { // User requested manual signup on additional data mismatch
                    console.debug('User requested manual signup on additional data mismatch, starting manual flow!');
                    const manualSignupSuccess = await this.manualSignup(verifiedPayload.userId, verifiedPayload.phone, true);
                    return false;
                }

                requestData = verifiedPayload;

                if (birthDateNeeded) {
                    const formattedDate = new Date(additionalInfo.birthDate).toISOString().split('T')[0];
                    requestData.birthDate = formattedDate;
                }
                if (ssn4Needed) {
                    requestData.ssn4 = additionalInfo.ssn4;
                }
                if (firstNameNeeded) {
                    if (!requestData.fullName) {
                        requestData.fullName = {};
                    }
                    requestData.fullName.firstName = additionalInfo.firstName;
                }

                this.sopoLoadingService.present('api');
                try {
                    const response = await this.apiService.verified1Click(requestData);
                    this.sopoLoadingService.dismiss('api');
                    return true;
                } catch (error) {
                    this.sopoLoadingService.dismiss('api');
                    const errorData2 = error?.error?.payload?.data;
                    const errorCode2 = errorData2?.errorCode;

                    console.debug('Verified response error code:', errorCode2);

                    if (errorData2 && errorCode2) {
                        switch (errorCode2) {
                            case 'OCE011': {
                                //Phone and Additional Info Required
                                console.debug('Verified - Additional info needed ', errorCode2);
                                const additionalInfoSuccess = await this.promptForInformation(requestData, errorData2);
                                return additionalInfoSuccess;
                            }
                            case 'OCE012': {
                                //Additional data mismatch
                                console.debug('Additional data MISMATCH');
                                const additionalInfoSuccess = await this.promptForInformation(requestData, errorData, true, true);
                                return additionalInfoSuccess;
                            }
                            case 'OCE013': {
                                //No Credentials Found
                                console.debug('No credentials found, starting manual flow!');
                                const manualSignupSuccess = await this.manualSignup(verifiedPayload.userId, verifiedPayload.phone);
                                return false;
                            }
                            case 'OCE019': {
                                //Maximum attempts exceeded
                                console.debug('Maximum attempts exceeded, starting manual flow!');
                                const manualSignupSuccess = await this.manualSignup(verifiedPayload.userId, verifiedPayload.phone);
                                return false;
                            }
                            default: {
                                this.showAlert({header: 'Verified call failed', message: 'Please try again later.'});
                                console.error(error);
                            }
                        }
                    }
                    else {
                        this.showAlert({header: 'Verified call failed', message: 'Please try again later.'});
                        console.error(error);
                    }
                }
            }
        }
        return false;
    }

    async manualSignup(userId: string, phoneNumber: string, skipPrompt = false) {
        if (!skipPrompt) {
            const infoResponse = await this.sopoPopupService.promptForConfirmation('app', {data: {
                header: "Verification Data Not Found",
                body: "We couldn’t retrieve your verified information automatically. Please enter your details manually to continue.",
                okText: "Ok",
                cancelText: "Cancel"
            }});
    
            if (!infoResponse) {
                return false;
            }
        }

        const dialogOptions: SopoVerifyDataSettings = {
            data: {
                header: 'Manual signup',
                body: 'Enter your info below.',
                okText: 'Confirm',
                cancelText: 'Cancel',
                phone: phoneNumber,
                isEditable: true,
            },
        }
        const response = await this.sopoPopupService.promptForVerifyDataInput('app', dialogOptions);

        if (response) {
            const verifiedResponse = response.verifiedData;

            const verifiedDataResponse: IEditVerifiedUserDataPayload = {
                firstName: verifiedResponse.firstName ? verifiedResponse.firstName : '',
                middleName: verifiedResponse.middleName ? verifiedResponse.middleName : '',
                lastName: verifiedResponse.lastName ? verifiedResponse.lastName : '',
                birthDate: verifiedResponse.birthDate ? (new Date(verifiedResponse.birthDate)).getTime() : null,
                address: {
                    city: verifiedResponse.city ?? '',
                    country: verifiedResponse.country ?? '',
                    line1: `${verifiedResponse.addressLine1 ?? ''} ${verifiedResponse.addressLine2 ?? ''}`.trim(),
                    state: verifiedResponse.state ?? '',
                    zipCode: verifiedResponse.zip ?? '',
                },
                ssn: verifiedResponse.ssn ?? '',
            };

            this.sopoLoadingService.present('api');
            try {
                await this.apiService.editVerifiedUserData(verifiedDataResponse, userId);
            } catch(error) {
                this.sopoLoadingService.dismiss('api');
                console.error('[ApiService] - edit verified data failed!', error);
                this.showAlert({header:'Data Confirmation Error', message: 'Something went wrong, please try again later. If it’s been longer than 24 hours please create a support ticket.'});
                return false;
            }

            //ACCEPTED
            try {
                await this.apiService.setUserVerifiedDataStatusAccepted(userId);
                this.sopoLoadingService.dismiss('api');
            } catch (error) {
                this.sopoLoadingService.dismiss('api');
                if (error.error.message && error.error.message === 'An error occured while saving your data, please try again!') {
                    this.showAlert({header: 'Data Confirmation Error', message: 'An error occured while saving your data, please try again.'});
                } else {
                    console.error('[ApiService] - set user verified status ACCEPTED failed!', error);
                    this.showAlert({header: 'Data Confirmation Error', message: 'Something went wrong, please try again later. If it’s been longer than 24 hours please create a support ticket.'});
                }
            }
        }
        return false;
    }

    async verifyPersona(userId: string) {
        try {
            const personaDocRef = doc(this.firestore, `kyc/${userId}`);
            const data = await firstValueFrom(docData(personaDocRef)) as IKycData;
            const inquiryId = data?.personaData?.inquiryId;

            if (!inquiryId) throw new Error('Persona inquiry id is missing!');

            const res = await this.startPersonaVerification(inquiryId);

            if (!res || !res.inquiryId || !res.status) {
                throw new Error('Persona plugin response is undefined!');
            }

            if (res.status === PersonaResponseStatus.COMPLETED) {
                console.debug('[VerificationService] - persona plugin completed!');
                this.showAlert({header:'Persona Verification', message:'Your verification is pending. Please check back later to see if it has been approved.'});
                this.twitterPixelService.kycCompletion();
            }
            else if (res.status === PersonaResponseStatus.CANCELLED) {
                if (res.sessionToken && res.inquiryId) {
                    localStorage.setItem('sessionToken', res.sessionToken);
                    localStorage.setItem('inquiryId', res.inquiryId);
                }
                console.debug('[VerificationService] - persona plugin canceled by user!');
                this.showAlert({header:'Persona Verification', message:'Verification canceled, please try again.'});
            }
            else if (res.status === PersonaResponseStatus.FAILED) {
                console.debug('[VerificationService] - persona plugin failed!');
                this.showAlert({header:'Persona Verification', message:'Verification failed! Please try again.'});
            }
        } catch(error) {
            console.error('[VerificationService] - verifyPersona', error);
            if (error.message === 'SopoPersona is not available on web!') {
                this.showAlert({header: 'Persona Verification Error', message: error.message});
            }
            let message = "Your KYC is pending, if it’s been longer than 24 hours please create a support ticket.";
            if (environment.staging) message = message + '<br/>' + `(${error.message})`;
            this.showAlert({header:'Persona Verification', message: message});
        }
    }

    async startPersonaVerification(inquiryId: string): Promise<PersonaResponse> {
        const prevSessionToken = localStorage.getItem('sessionToken');
        const prevInquiryId = localStorage.getItem('inquiryId');

        if (prevSessionToken && prevInquiryId === inquiryId) {
            return new Promise((resolve, reject) => {
                const client = new Persona.Client({
                    inquiryId: inquiryId,
                    sessionToken: prevSessionToken,
    
                    onComplete: ({ inquiryId, fields }) => {
                        resolve({
                            inquiryId,
                            status: PersonaResponseStatus.COMPLETED,
                            fields
                        });
                    },
    
                    onCancel: ({ inquiryId, sessionToken }) => {
                        resolve({
                            inquiryId,
                            status: PersonaResponseStatus.CANCELLED,
                            sessionToken: sessionToken
                        });
                    },
    
                    onError: (error: any) => {
                        console.error('[AuthStoreDispatcher] - startPersonaVerification', error);
                        reject({
                            inquiryId: null,
                            status: PersonaResponseStatus.FAILED
                        });
                    },
                });
    
                client.open();
            });
        }

        return new Promise((resolve, reject) => {
            const client = new Persona.Client({
                inquiryId: inquiryId,

                onComplete: ({ inquiryId, fields }) => {
                    resolve({
                        inquiryId,
                        status: PersonaResponseStatus.COMPLETED,
                        fields
                    });
                },

                onCancel: ({ inquiryId, sessionToken }) => {
                    resolve({
                        inquiryId,
                        status: PersonaResponseStatus.CANCELLED,
                        sessionToken: sessionToken
                    });
                },

                onError: (error: any) => {
                    console.error('[AuthStoreDispatcher] - startPersonaVerification', error);
                    reject({
                        inquiryId: null,
                        status: PersonaResponseStatus.FAILED
                    });
                },
            });

            client.open();
        });

    }

    showAlert(sopoAlertSettings: SopoAlertSettings) {
        sopoAlertSettings.dismissAfter = DEFAULT_DISMISS_AFTER_TIME;
        this.sopoPopupService.clearViewContainer('app');
        this.sopoPopupService.showAlert('app', sopoAlertSettings);
    }
}

enum PersonaResponseStatus {
    COMPLETED = 'completed',
    CANCELLED = 'cancelled',
    FAILED = 'failed',
}

interface PersonaResponse {
    status: PersonaResponseStatus;
    inquiryId: string;
    fields?: any;
    sessionToken?: any;
}
